import ExpandMoreSharp from "@mui/icons-material/ExpandMoreSharp";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { DetectedEffect, ExtractedColor, Region } from "../../types";
import { ActiveRegionCard } from "./ActiveRegionCard";
import { ColorPalettePanel } from "./ColorPalettePanel";
import { CompactImageUploader } from "./CompactImageUploader";
import { DetectedEffectsPanel } from "./DetectedEffectsPanel";
import { ReferenceImageCard } from "./ReferenceImageCard";

const cardSx = {
  backgroundColor: "#ffffff",
  borderRadius: "var(--radius-card, 8px)",
  border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
  overflow: "hidden",
};

interface UploadMatchAccordionProps {
  /**
   * Whether a reference image is currently loaded. `false` after the
   * "Discard Progress" reset (CX-2908) — renders the compact drag-drop
   * uploader in place of the reference image / palette / detected-effects
   * cards, so the user can drop a new file without leaving the results view.
   */
  hasReferenceImage: boolean;
  imageSrc: string;
  palette: ExtractedColor[];
  selectedColorIndex: number;
  onColorSelect: (index: number) => void;
  detectedEffects?: DetectedEffect[];
  onChangeClick: () => void;
  activeRegion?: Region;
  regions?: Region[];
  onRegionClick?: (regionId: string) => void;
}

export const UploadMatchAccordion = ({
  hasReferenceImage,
  imageSrc,
  palette,
  selectedColorIndex,
  onColorSelect,
  detectedEffects,
  onChangeClick,
  activeRegion,
  regions,
  onRegionClick,
}: UploadMatchAccordionProps) => {
  const { t } = useTranslation();
  const isMultiRegion = !!regions && regions.length > 1;

  return (
    <Accordion sx={{ boxShadow: "none" }} defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreSharp />}>
        <Typography>{t("ldp_discovery_tab_image_title")}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {!hasReferenceImage ? (
          <CompactImageUploader />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {/* Reference image: always shown so every user has a Change button,
                and always carries the region outline(s) — including the
                single-region case, so the user can see where the analyzed
                region sits on the image. */}
            <Box sx={cardSx}>
              <ReferenceImageCard
                imageSrc={imageSrc}
                onChangeClick={onChangeClick}
                regions={regions}
                activeRegionId={activeRegion?.id}
                onRegionClick={onRegionClick}
              />
            </Box>

            {/* Active region cropped thumbnail — only useful when there's
                more than one region to distinguish. */}
            {isMultiRegion && activeRegion && (
              <Box
                sx={{
                  ...cardSx,
                  border: `1px solid ${activeRegion.color}`,
                }}
              >
                <ActiveRegionCard imageSrc={imageSrc} region={activeRegion} />
              </Box>
            )}

            {/* Extracted color palette card */}
            <Box sx={{ ...cardSx, padding: "14px" }}>
              <ColorPalettePanel
                palette={palette}
                selectedIndex={selectedColorIndex}
                onSelect={onColorSelect}
              />
            </Box>

            {/* Detected effects card */}
            {detectedEffects && detectedEffects.length > 0 && (
              <Box sx={{ ...cardSx, padding: "14px" }}>
                <DetectedEffectsPanel effects={detectedEffects} />
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
